import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import groupBy from 'object.groupby'
import { ErrorBoundary } from 'react-error-boundary'

import { getUpsellFooterWording, getUpsellWording } from '../../utils'
import useTrackingContext from '../../hooks/useTrackingContext'
import useOrganisationCaps from '../../hooks/useOrganisationCaps'

import ErrorPage from '../ErrorPage'
import ProgressBar from '../ProgressBar'
import Header from '../Header'
import UpsellForm from './Form'
import useScrolledToTop from '../../hooks/useScrolledToTop'
import Footer from '../Footer'

import { ORGANISATIONS_PER_UPSELL_ROUND } from '../../constants'
import OrderAllButton from './OrderAllButton'
import { UpsellContextProvider } from '../../UpsellContext'

const Upsell = (props) => {
  const {
    prechecked,
    businessArea,
    interestInSchoolPrograms,
    initialOrder,
    organisations,
    upsellRounds,
    setUpsellRounds,
    onFinish,
    afterSubmit
  } = props

  const tracker = useTrackingContext()

  const onUpsellFormCancel = () => {
    tracker.trackEvent('CatalogOrder', 'Upsellform canceled')
    onFinish()
  }

  const [upsellRound, setUpsellRound] = useState(0)
  const [upsellCondition, setUpsellCondition] = useState()

  const wording = getUpsellWording(businessArea, upsellRound)
  const footerWording = getUpsellFooterWording(businessArea, upsellRound)

  useEffect(() => {
    if (businessArea == 'schueleraustausch') {
      setUpsellCondition(initialOrder.exchange_country_code)
    }

    if (businessArea == 'sprachreisen') {
      setUpsellCondition(initialOrder.target_group)
    }
  }, [businessArea])

  const {
    isLoading,
    isError,
    error,
    data: organisationCaps
  } = useOrganisationCaps(businessArea)

  // Keep track of which organisations the user has already seen
  const [seenOrganisationSlugs, setSeenOrganisationSlugs] =
    useState([initialOrder.organisation_slug])

  const scrolledToTop = useScrolledToTop(-130)

  if (isLoading) {
    return (
      <p className='text text-warning'>Formular wird geladen...</p>
    )
  }

  if (isError) {
    throw error
  }

  const cappedOrganisationSlugs =
    organisationCaps
      .filter(({ cap_reached }) => cap_reached)
      .map(({ slug }) => slug)

  let relevantOrganisations = organisations

  // Filter out organisations not matching business area specific values from initial order
  switch (businessArea) {
    case 'schueleraustausch':
      relevantOrganisations = organisations
        .filter((orga) => {
          return orga.target_countries.includes(initialOrder.exchange_country_code)
        })
        .filter(orga => {
          const schoolProgramsUpsellOnly = orga.additional_config.school_programs_upsell_only

          // Organisations that offer school programs,
          // shall not appear in regular upsell
          return !schoolProgramsUpsellOnly
              || (interestInSchoolPrograms && schoolProgramsUpsellOnly)
        })
      break
    case 'sprachreisen':
      relevantOrganisations = organisations.filter((orga) => {
        return orga.target_groups.includes(initialOrder.target_group)
      })
      break
  }


  const uncappedOrganisations = relevantOrganisations
    .filter(({ slug }) => {
      let isCapped = !cappedOrganisationSlugs.includes(slug)
      return isCapped || (slug === props.organisationId)
    })

  const priorityMap = groupBy(uncappedOrganisations, ({ priority_placements }) => {
    return priority_placements.find(({ context }) => context === 'upsell')
    ? 'prioritized'
    : 'regular'
  })

  const upsellOrganisations = [...(priorityMap.prioritized ||[]),
                               ...(priorityMap.regular || [])]

  // How many rounds of upsell will be have?
  setUpsellRounds(Math.floor(upsellOrganisations.length / ORGANISATIONS_PER_UPSELL_ROUND))

  // Contains the organisations for the current upsell round
  const organisationsToShow = upsellOrganisations
    .filter(({ slug }) => {
      return !seenOrganisationSlugs.includes(slug)
    })
    .slice(0, ORGANISATIONS_PER_UPSELL_ROUND)

  const handleUpsellCreated = ({ seenOrganisations, orderedOrganisations, orderedOrganisationCount }) => {
    let nextRound = upsellRound + 1

    afterSubmit({ seenOrganisations, orderedOrganisations, orderedOrganisationCount })

    const newlySeenOrganisationSlugs = seenOrganisations.map(({ slug }) => {
      return slug
    })

    tracker.trackEvent('CatalogOrder', 'Upsell created -  round ' + upsellRound + ', condition: ' + upsellCondition + ', slugs: ' + organisationsToShow.map((o) => o.slug) )

    setSeenOrganisationSlugs(seenOrganisationSlugs.concat(newlySeenOrganisationSlugs))
    setUpsellRound(nextRound)
  }

  if (organisationsToShow.length === 0) {
    // we're done, so just finish now
    tracker.trackEvent('CatalogOrder', 'Process complete - no more upsellable orgas for condition: ' + upsellCondition + ', seen: ' + seenOrganisationSlugs.join(', ') )

    onFinish()
    return (<></>)
  }
  else {
    tracker.trackEvent('CatalogOrder', `Upsell show - Round ${upsellRound}`)

    tracker.trackEvent('CatalogOrder', 'Upsell show - condition: ' + upsellCondition + ' -  ' + organisationsToShow.map((o) => o.slug).join(', '))

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <UpsellContextProvider
        round={upsellRound}
        businessArea={businessArea}
        initialOrder={initialOrder}
        onSuccess={handleUpsellCreated}
        upsellCount={props.upsellCount}
      >
      <div ref={scrolledToTop}>
        <ProgressBar max={upsellRounds} current={upsellRound} />

        <Header wording={wording} />
        {/*
            `key` is essential here in order to recreate this component
            on subsequent Upsell rounds. Otherwise the constructor is __NOT__ called
            and  the initial state is __NOT__ reset (and the checkboxes of round 2
            and later are unchecked).

            See: https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#alternative-1-reset-uncontrolled-component-with-an-id-prop

          */}
        <UpsellForm
          key={upsellRound}
          organisations={organisationsToShow}
          onCancel={onUpsellFormCancel}
          prechecked={prechecked}
        />
        <Footer text={footerWording.text} />
        <OrderAllButton organisations={organisationsToShow} />
      </div>
      </UpsellContextProvider>
    </ErrorBoundary>
  )
  }
}

Upsell.propTypes = {
  organisations: PropTypes.array.isRequired,
  prechecked: PropTypes.bool.isRequired,
  businessArea: PropTypes.string.isRequired,
  interestInSchoolPrograms: PropTypes.bool.isRequired,
  initialOrder: PropTypes.object.isRequired,
  onFinish: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func.isRequired,
  // How many orders via upsell have been created up to now
  upsellCount: PropTypes.number.isRequired,
  upsellRounds: PropTypes.number.isRequired,
  setUpsellRounds: PropTypes.func.isRequired,
}

export default Upsell
